import { config } from './config';
import { getIdToken } from './auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }

  /**
   * The caller is signed in but has no X5 organization yet. Its own state rather
   * than an error: the portal shows the account-setup panel for it, which is the
   * behaviour the GraphQL version got by throwing the string
   * 'Not assigned to an Organization'.
   */
  get isNotProvisioned(): boolean {
    return this.code === 'NOT_PROVISIONED';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, 'Not signed in', 'UNAUTHORIZED');

  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const shape = (data ?? {}) as { error?: unknown; code?: unknown };
    const message =
      typeof shape.error === 'string' ? shape.error : `Request failed: ${res.status}`;
    throw new ApiError(
      res.status,
      message,
      typeof shape.code === 'string' ? shape.code : undefined,
    );
  }

  return data as T;
}

export interface Me {
  username: string;
  /** From the verified ID token, not a DynamoDB row. Null if the claim is absent. */
  email: string | null;
  organizationId: string;
  organization: { id: string; name: string; company: string | null } | null;
}

/** Only id and displayName — the API no longer discloses fleet topology. */
export interface AppSummary {
  id: string;
  displayName: string;
}

export interface SupportToken {
  /** Short-lived and single-purpose: it identifies the caller to Zendesk, nothing else. */
  token: string;
  expiresIn: number;
  /**
   * Advisory. The browser applies this as a conversation tag, so Zendesk has no
   * way to attest it — good enough for routing and triage, not for authorization.
   * Null when the caller has no organization yet.
   */
  organization: { id: string; name: string } | null;
}

export interface SessionResponse {
  embedUrl: string;
  nativeClientLink: string;
  displayName: string;
  reference: string;
}

export const api = {
  me: () => request<Me>('GET', '/me'),

  /**
   * One call, unpaginated from the browser's point of view. The GraphQL version
   * walked `nextToken` in a loop here; the API does that server-side now.
   */
  listApplications: () => request<{ items: AppSummary[] }>('GET', '/me/applications'),

  /**
   * Minted server-side on every call rather than cached: the signing secret can
   * never reach the browser, and the widget asks again whenever its token
   * expires. Answers 503 until a Zendesk signing key is configured.
   */
  supportToken: () => request<SupportToken>('GET', '/me/support-token'),

  /**
   * An application id is the entire request. The old endpoint took `fleet`,
   * `stack`, `application`, `x5serverIP` and `user` from the browser and trusted
   * all five, so any signed-in user could stream from any fleet. The server now
   * reads every one of them off the row after checking it belongs to the
   * caller's organization.
   */
  createSession: (applicationId: string) =>
    request<SessionResponse>('POST', '/appstream/session', { applicationId }),
};
